import type * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

// Drop-in replacement for the browser's `window.confirm()` that matches the
// rest of the app's styled UI. Controlled component — caller owns the open
// state and the action callback. Use `destructive` for delete-style flows
// to swap the confirm button to a destructive variant.
//
// Pattern:
//   const [pendingDelete, setPendingDelete] = useState<Item | null>(null);
//   ...
//   onClick={() => setPendingDelete(item)}
//   <ConfirmDialog
//     open={pendingDelete !== null}
//     onOpenChange={(o) => !o && setPendingDelete(null)}
//     title="Delete pay plan?"
//     description={`This will permanently remove "${pendingDelete?.payPlan}".`}
//     confirmLabel="Delete"
//     destructive
//     onConfirm={() => {
//       if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
//       setPendingDelete(null);
//     }}
//   />
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={destructive ? buttonVariants({ variant: "destructive" }) : undefined}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
